/**
 * QuestManualCheckPage — 미션수동확인(마이그146).
 *
 * 오퍼레이터가 고객의 미션 수행(예: 네이버 평점 올리기)을 육안 확인한 뒤,
 * 매장 active manual_check 스팟 목록에서 해당 미션을 선택하고 고객 QR을 스캔하면
 * 그 고객의 미션을 완료 처리(태깅+보상)한다.
 *
 * 흐름: 목록 로드 → 스팟 선택 → 고객 QR 스캔(USB 리더 우선, 카메라 폴백) → 완료 → 목록 복귀.
 * 헤더와 색상은 주렁은행 작업 화면과 동일한 톤을 사용한다.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { extractQuestScanCode } from '../utils/qrUtils';
import { getManualCheckSpots, completeManualCheck } from '../services/api';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ManualCheckDesk from './ManualCheckDesk';
import './QuestManualCheckWorkspace.css';

const PHASE_LIST = 'list';   // 스팟 선택
const PHASE_SCAN = 'scan';   // 고객 QR 스캔

// 모든 환경에서 QR 리더 대기로 시작하며, 카메라는 공용 버튼으로 직접 연다.

export default function QuestManualCheckPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const storeName = user?.store_name || localStorage.getItem('store_name') || '';
  const [phase, setPhase] = useState(PHASE_LIST);
  const [activeSet, setActiveSet] = useState(null);
  const [spots, setSpots] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);   // {ok, text}
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await getManualCheckSpots()) || {};
      setActiveSet(data.active_set || null);
      setSpots(Array.isArray(data.spots) ? data.spots : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSelectSpot = (spot) => {
    setSelectedSpot(spot);
    setFeedback(null);
    setPhase(PHASE_SCAN);
  };

  const backToList = useCallback(() => {
    setSelectedSpot(null);
    setFeedback(null);
    setPhase(PHASE_LIST);
  }, []);

  const handleScan = useCallback(async (rawOrCode) => {
    if (!selectedSpot || busy) return;
    // 퀘스트 전용 합성 파서 — 여권(ZLP_)까지 받는다. 공유 extractQRCode 는 넓히지 않는다(S10)
    const qr = extractQuestScanCode(rawOrCode) || rawOrCode;
    if (!qr) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await completeManualCheck(qr, selectedSpot.id);
      const dup = res?.already_tagged;
      setFeedback({
        ok: true,
        text: dup
          ? '이미 완료된 회원입니다.'
          : `${res?.message || '수동확인 완료!'} (${res?.done ?? '-'}/${res?.total ?? '-'})`,
      });
    } catch (e) {
      setFeedback({ ok: false, text: e?.response?.data?.message || e?.message || '완료 처리 실패' });
    } finally {
      setBusy(false);
    }
  }, [selectedSpot, busy]);

  // USB QR 리더기 — 현장 태블릿 기본 입력(카메라는 폴백). 스캔 페이지·비통신·비피드백 중에만.
  useUsbQrReader({
    enabled: phase === PHASE_SCAN && !busy && !feedback,
    onScan: (raw) => {
      const code = extractQuestScanCode(raw);
      if (code) handleScan(code);
    },
  });

  return (
    <div className="qmc-page">
      <header className="qmc-workspace-header">
        <div className="qmc-workspace-header-left">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>
            메뉴
          </Button>
          <h1>미션 수동확인</h1>
        </div>
        <div className="qmc-workspace-header-right">
          {storeName && <span className="qmc-workspace-store"><i aria-hidden="true" />{storeName}</span>}
          <span className="qmc-workspace-user"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>

      <ManualCheckDesk
        scanning={phase === PHASE_SCAN && !!selectedSpot}
        activeSet={activeSet}
        spots={spots}
        selectedSpot={selectedSpot}
        loading={loading}
        error={error}
        busy={busy}
        feedback={feedback}
        onReload={load}
        onSelect={handleSelectSpot}
        onScan={handleScan}
        onBack={backToList}
        onNext={() => setFeedback(null)}
      />
    </div>
  );
}
